import {
    partnerCockpitReimbursementEligibilitySchema,
    type PartnerCockpitReimbursementEligibility,
    reimbursementProofDocumentSchema,
    type PartnerCockpitReimbursementRequest,
    type ReimbursementProofDocument,
} from '@houzy/schemas/partner-cockpit/reimbursement';
import {
    queryOptions,
    useMutation,
    useQueryClient,
    type UseMutationResult,
    type UseQueryOptions,
    type UseQueryResult,
    useQuery,
} from '@tanstack/react-query';

import { apiRequest } from '../../../lib/api';
import { partnerCockpitApiPath } from '../../../lib/config';

type EligibilityQueryKey = readonly ['reimbursement', 'eligibility', string];

export function reimbursementEligibilityQueryOptions(
    offerRequestLeadUuid: string,
): UseQueryOptions<
    PartnerCockpitReimbursementEligibility,
    Error,
    PartnerCockpitReimbursementEligibility,
    EligibilityQueryKey
> {
    return queryOptions({
        queryKey: ['reimbursement', 'eligibility', offerRequestLeadUuid] as const,
        queryFn: ({ signal }): Promise<PartnerCockpitReimbursementEligibility> =>
            apiRequest(partnerCockpitApiPath(`reimbursements/${offerRequestLeadUuid}/eligibility`), {
                signal,
                responseSchema: partnerCockpitReimbursementEligibilitySchema,
            }),
    });
}

export function useReimbursementEligibility(
    offerRequestLeadUuid: string,
): UseQueryResult<PartnerCockpitReimbursementEligibility> {
    return useQuery(reimbursementEligibilityQueryOptions(offerRequestLeadUuid));
}

export function useRequestReimbursement(
    offerRequestLeadUuid: string,
): UseMutationResult<void, Error, PartnerCockpitReimbursementRequest> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request: PartnerCockpitReimbursementRequest): Promise<void> =>
            apiRequest(partnerCockpitApiPath(`reimbursements/${offerRequestLeadUuid}`), {
                method: 'POST',
                body: { request },
            }),
        onSuccess: async (): Promise<void> => {
            await queryClient.invalidateQueries({ queryKey: ['reimbursement', 'eligibility', offerRequestLeadUuid] });
            await queryClient.invalidateQueries({ queryKey: ['offerRequests'] });
        },
    });
}

export function useUploadReimbursementProof(
    offerRequestLeadUuid: string,
): UseMutationResult<ReimbursementProofDocument, Error, File> {
    return useMutation({
        mutationFn: (file: File): Promise<ReimbursementProofDocument> => {
            const body = new FormData();
            body.append('file', file);
            return apiRequest(partnerCockpitApiPath(`reimbursements/${offerRequestLeadUuid}/proof-documents`), {
                method: 'POST',
                body,
                responseSchema: reimbursementProofDocumentSchema,
            });
        },
    });
}
